import React from 'react';
import styled from 'styled-components';
import {FormattedMessage, injectIntl} from 'react-intl';
import {connect} from 'react-redux'
import H1 from "../../Components/H1";
import {ToolbarWithLogo} from '../../Components/Toolbar';
import ContainerPage from "../ContainerPage";
import {cancelSession, resetWizard} from '../Welcome/Actions';
import {BottomWrapper, SelectViewWrapper, TopWrapper} from '../../Components/Layout';
import PoweredBy from "../../Components/PoweredBy";
import ProgressTracker from "../../Components/ProgressTracker";
import Toast from "../../Components/Toast";
import {getSafely, isNumber, valueOrDefault} from "@tomeravni/easybizy-js-common/common";
import Sizes from "../../Assets/Sizes";
import H2 from "../../Components/H2";
import TimerCounter from "../../Components/TimerCounter";
import {NavigationBottomButton} from "../../Components/NavigationButton";
import P from "../../Components/P";
import {Button} from "rmwc/Button/index";
import Confirm from "../../Components/Confirm";
import {PAYMENT_TYPE} from "../../Common/constants";
import {TwoWaysCenterContainer} from "../Identify/CommonUI";

const HH1 = styled(H1)`
    z-index: 1;
    text-align: center;
    width: 100%;
`;

const PP = styled(P)`
    padding: 0;
`;

const HH2 = styled(H2)`
    padding-top: 0;
`;

const FlexWrapper = styled.div`
    padding: 0 ${Sizes.defaultPadding};
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    ${p => p.spaceTop && `margin-top: ${Sizes.footerPoweredByMinHeight}`}
`


const PaymentPageWrapper = styled.div`
    width: 100%;
    flex-grow: 1;
    padding: 0 ${Sizes.mediumPadding};
    position: relative;
    margin-bottom: ${Sizes.mediumPadding};

    iframe {
        width: 100%;
        height: 100%;
        border: none;
        box-shadow: none
    }
`;


const SecondaryButton = styled(Button).attrs({
    className: 'existing-meetings-dialog__secondary-button',
})`
    --mdc-theme-primary: ${p => p.theme.colors.primaryText};

    height: auto;
    padding-top: ${Sizes.smallMargin};
    padding-bottom: ${Sizes.smallMargin};
    border-radius: ${Sizes.defaultBorderRadius};
    font-family: inherit;
    border: none;
    font-weight: 400;
    color: ${p => p.theme.colors.primaryText};
    text-decoration: underline;

`;

class Payment extends ContainerPage {
    state = {};

    componentDidMount() {
        super.componentDidMount();
        const handleMessage = (event) => {
            if (event.data.source === 'pci-client' && event.data.event === 'payment-link-paid') {
                window.removeEventListener('message', handleMessage, false);
                this.done();
            }

        };

        window.addEventListener('message', handleMessage, false);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        super.componentDidUpdate(prevProps, prevState, snapshot);
        if (this.props.errorCancelingSession) {
            setTimeout(() => {
                this.toast.getWrappedInstance().show(this.props.errorCancelingSession.message)
            })
        }

    }

    /**
     * The countdown reached zero, so the slot is no longer being held for this customer.
     *
     * Releasing it is asked for here rather than left to the cron: the customer is about to be sent
     * back to the start, and if the meetings were still on the calendar at that point the very next
     * screen would offer them a time that already looks taken — by themselves. The cron still runs,
     * this only stops the gap being visible.
     *
     * If the release fails the customer stays put with the error, because sending them away while
     * their meetings are still standing is the exact situation being avoided. The window is small:
     * the payment link dies at this same moment, so nothing can be charged in the meantime and the
     * cron picks the session up regardless.
     *
     * A session that held nothing to remove (`removed: 0`), or was already cancelled, has nothing
     * standing either, so the customer is sent back the same as after a real release.
     */
    handleTimeUp = () => {
        this.props.cancelSession((res) => {
            if (!isNumber(getSafely(['removed'], res))) {
                return;
            }

            this.props.reset();
            setTimeout(() => {
                this.startOver()
            }, 200)
        });
    }

    cancelMeeting() {
        let title, body;

        title = (<FormattedMessage id="schedule.removeMeetingsTitle"/>);
        body = (<FormattedMessage id="schedule.removeMeetingsBody"/>);


        this.dialog.show(title, body, (accept) => {
            if (accept) {
                this.props.cancelSession((res) => {
                    if (res.removed) {
                        this.props.reset();
                        setTimeout(() => {
                            this.startOver()
                        }, 200)
                    }
                });

            }
        });
    }

    render() {
        const ltr = !this.props.intl.locale.includes('he');
        const currentProgressIndex = valueOrDefault(this.props.progress, []).length - 1
        const sessionError = getSafely(['props', 'errorOnSession', 'message'], this);
        const translatedError = sessionError && this.props.intl.formatMessage({id: sessionError});
        const isValidatePayment = this.props.paymentType === PAYMENT_TYPE.VALIDATE;
        const toolbarLogo = getSafely(['theme', 'properties', 'avatarImage'], window) || (this.props.config && this.props.config.Logo) || '';

        /**
         * The last known url is held on to, so a session action that momentarily leaves it empty
         * cannot unmount the iframe below. Remounting it would load the payment page a second time,
         * and each load takes the payment link - the second one then finds it already in use.
         */
        if (this.props.paymentUrl) {
            this.paymentUrl = this.props.paymentUrl;
        }

        return (
            <div className="transition-item detail-page detail-page__payment">
                <SelectViewWrapper padding={true} bottomPadding={false} loading={!this.props.config} ltr={ltr}>
                    <ToolbarWithLogo imageUri={toolbarLogo} ltr={ltr}
                                     rootUri={this.getRoutePrefix()}>
                        <ProgressTracker progress={this.props.progress} current={currentProgressIndex}
                                         visible={true}/>

                    </ToolbarWithLogo>

                    <TopWrapper justify="start" align="start" preventFlex={true}>
                        <HH1><FormattedMessage
                            id={isValidatePayment ? 'validation_payment' : 'schedule.payment'}/></HH1>
                        {!this.props.loading && !sessionError && <FlexWrapper>
                            <div className="default-margin__bottom">
                                <HH2><FormattedMessage id="schedule_save_your_spot"/></HH2>
                                <PP><FormattedMessage id="schedule_appointment_will_expire"/></PP>
                            </div>
                            <div>
                                {/* Counts down to the real expiry. It used to stop a minute short, to
                                    match a server side lookup that treated a session as gone a minute
                                    early; both are gone, so the customer now sees the time they
                                    actually have. */}
                                {this.props.expiresOn && <TimerCounter
                                    onTimeUp={this.handleTimeUp}
                                    destinationTime={this.props.expiresOn}/>}

                                <SecondaryButton onClick={this.cancelMeeting.bind(this)}>
                                    <FormattedMessage id="cancelMeeting"/>
                                </SecondaryButton>

                            </div>
                        </FlexWrapper>}
                        {sessionError &&
                            <TwoWaysCenterContainer>
                                <div>
                                    <FlexWrapper><H2>{translatedError}.</H2></FlexWrapper>
                                    <FlexWrapper
                                        spaceTop={true}><NavigationBottomButton onClick={this.reset}><FormattedMessage
                                        id="schedule_new_meeting"/></NavigationBottomButton></FlexWrapper>
                                </div>
                            </TwoWaysCenterContainer>}
                        <PaymentPageWrapper>
                            {this.paymentUrl && <iframe
                                title={this.props.intl.formatMessage({id: 'schedule.payment'})}
                                src={`${this.paymentUrl}?lightMode=true`}></iframe>
                            }
                        </PaymentPageWrapper>
                    </TopWrapper>
                    <BottomWrapper empty={true}>
                        <PoweredBy/>
                    </BottomWrapper>
                </SelectViewWrapper>
                <Confirm ref={dialog => this.dialog = dialog} ltr={ltr}/>

                <Toast ref={(toast) => this.toast = toast}/>
            </div>
        )
    }
}

/** Styled-Components **/

/** END OF Styled-Components **/

const mapStateToProps = state => {
    return {
        loading: state.schedule.requestingSession,
        config: state.schedule.configWithServices,
        progress: state.schedule.progress,
        paymentUrl: state.schedule.paymentUrl,
        paymentType: state.schedule.paymentType,
        expiresOn: state.schedule.paymentExpiresOn,
        errorOnSession: state.schedule.sessionError,
        errorCancelingSession: state.schedule.cancelSessionError,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        cancelSession: (callback) => dispatch(cancelSession(callback)),
        reset: () => dispatch(resetWizard()),

    }
};


export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(Payment));
